import { useGetClientByIdQuery, useUpdateClientMutation } from '@entities/clients/rtk-query.ts';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useEffect, useState } from 'react';
import { useClientContactModal } from '@widgets/clients/client_contact_modal/helpers.ts';
import {
  BASE_URL_MAP,
  MAP_PLACEHOLDERS,
  VALIDATION_PATTERNS,
  type ContactType,
} from '@widgets/contacts/contact-edit/constans.tsx';
import { SelectType } from '@features/contacts/index.ts';
import { doTransformRTKErrorResponse, isPhoneNumber } from '@shared/helpers/index.ts';
import type { ContactItem } from '@shared/types/contacts';
import { TextInputM } from '@shared/ui/index.ts';
import { ModalSmall } from '@src/library/components/common/index.ts';
import * as st from './styles.module.scss';

export const ClientContactModal = () => {
  const { state, closeClientContactModal } = useClientContactModal();
  const isCreating = state?.type === 'create';
  const { data: client } = useGetClientByIdQuery(state?.clientId as number, {
    skip: state?.clientId === undefined,
  });

  const [contactOption, setContactOption] = useState<ContactType>('phone');
  const [contactValue, setContactValue] = useState('');

  const [contact, setContact] = useState<Partial<ContactItem>>(state?.contactData || {});

  const [error, setError] = useState('');

  const [updateClientMutation, { isLoading: isClientUpdating }] = useUpdateClientMutation();

  const isNumberOption =
    contactOption === 'phone' || contactOption === 'viber' || contactOption === 'whatsapp';

  useEffect(() => {
    if (state?.contactData) {
      const contactData = state.contactData;
      setContactOption(contactData.type || 'phone');
      setContactValue(contactData.value);
      setContact(contactData);
      setError('');
    }
  }, [state, setContactOption, setContactValue, setContact, setError]);

  const resetModal = () => {
    closeClientContactModal();
    setContactOption('phone');
    setContactValue('');
    setContact({});
    setError('');
  };
  const validationValue = (value: string) => {
    const pattern = VALIDATION_PATTERNS[contactOption];
    if ((isNumberOption && value.length > 11) || value === '') {
      setError(' ');
    } else if (!pattern.test(value)) {
      setError(' ');
    } else {
      setError('');
    }
  };

  const handleChangeType = (type: ContactType) => {
    setContactOption(type);
    setContact((prev) => ({ ...prev, type, base_url: BASE_URL_MAP[type] }));
  };

  const handleChangeContactValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    validationValue(value);
    setContactValue(value);
    setContact((prev) => ({
      ...prev,
      value,
    }));
  };

  const submit = async () => {
    if (state?.clientId && client) {
      const newActiveContacts = client?.active_contacts ? [...client?.active_contacts] : [];

      if (isCreating) {
        newActiveContacts.push(contact as ContactItem);
      } else {
        const lookedIndex = newActiveContacts?.findIndex((el) => el.id === contact?.id);
        if (lookedIndex) {
          newActiveContacts[lookedIndex] = contact as ContactItem;
        }
      }
      const contactsToBack = newActiveContacts.map((c) => ({ ...c, user: undefined }));
      const formData = new FormData();
      formData.append(`active_contacts`, JSON.stringify(contactsToBack));
      const resp = await updateClientMutation({ body: formData, id: state!.clientId });
      if (resp?.error) {
        const transformedError = doTransformRTKErrorResponse(resp?.error as FetchBaseQueryError);
        const errMessage = transformedError?.firstError;
        errMessage && setError(errMessage);
      } else {
        resetModal();
      }
    } else {
      throw new Error('It is now the correct way to use this modal!');
    }
  };

  return (
    <ModalSmall
      isOpen={state?.isOpen || false}
      needClose={true}
      title={isCreating ? 'Новый контакт' : 'Изменение контакта'}
      onRequestClose={resetModal}
      buttonInHeader={{
        children: 'Готово',
        onClick: submit,
        disabled: isClientUpdating || Boolean(error),
      }}
      modalBodyClassName={st.body}
    >
      <SelectType
        wrapperClassName={st.mobileSelect}
        contactOption={contactOption}
        setContactOption={handleChangeType}
      />
      <TextInputM
        labelClassName={st.valueLabel}
        label={'URL / телефон'}
        placeholder={MAP_PLACEHOLDERS[contactOption]}
        value={
          isPhoneNumber(contactValue)
            ? contactValue.replace(/ /g, '').replace(/-/g, '')
            : contactValue
        }
        onChange={handleChangeContactValue}
        error={error}
      />
    </ModalSmall>
  );
};
